'use client';

import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { Box, Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Card } from '@osmosis/tailwind-ui/components/Card';
import { H2 } from '@osmosis/tailwind-ui/components/Text';
import { cn } from '@osmosis/tailwind-ui/helpers/classes';
import { text5 } from '@osmosis/tailwind-ui/styles/text';
import { bg0, bg1, bg2 } from '@osmosis/tailwind-ui/styles/background';

import LearnSidenav, {
  type Props as LearnSidenavProps,
} from '../sidenav/LearnSidenav';
import Divider from '../../Divider';

type Props = Pick<LearnSidenavProps, 'name' | 'sidebar' | 'isLoggedInView'>;

type UpNextTab = {
  id: string;
  label: string;
  content: React.ReactNode;
};

/**
 * Right-rail "Up next" panel for the v2 Learn page. Owns the heading and the
 * (future) tab control, and encapsulates the content of each tab.
 *
 * Currently it surfaces a single "Related" tab backed by {@link LearnSidenav}.
 * Additional tabs (e.g. "My schedule") slot into the `tabs` array below — the
 * segmented control renders automatically once more than one tab exists.
 */
const UpNextPanel = ({
  name,
  sidebar,
  isLoggedInView,
}: Props): JSX.Element | null => {
  const { formatMessage } = useIntl();

  const tabs: UpNextTab[] = [
    {
      id: 'related',
      label: formatMessage({
        id: 'learn.up_next.related',
        defaultMessage: 'Related',
      }),
      content: (
        <LearnSidenav
          name={name}
          sidebar={sidebar}
          isLoggedInView={isLoggedInView}
          hideHeader
        />
      ),
    },
  ];

  const [activeTabId, setActiveTabId] = useState(tabs[0]?.id);
  const activeTab = tabs.find((tab) => tab.id === activeTabId) ?? tabs[0];

  // Nothing to surface — don't render an empty "Up next" card.
  if (!sidebar?.tabs?.length) return null;

  return (
    <Card
      data-testid="learn-up-next"
      className={cn(
        'flex h-full min-h-0 flex-col gap-2 overflow-hidden px-0 pt-2.5 pb-0 rounded-none md:rounded-lg md:max-h-[56vh]',
      )}
    >
      {/* Heading + divider are redundant on mobile, where the section overlay
          provides its own "Related" header — hide them below md. */}
      <H2
        className={cn(
          'hidden shrink-0 !text-lg !font-bold px-2 md:block',
          text5,
        )}
      >
        {formatMessage({
          id: 'learn.up_next.title',
          defaultMessage: 'Up next',
        })}
      </H2>

      <Box className="hidden md:block">
        <Divider className={bg2} />
      </Box>

      {tabs.length > 1 && (
        <Flex
          role="tablist"
          aria-label={formatMessage({
            id: 'learn.up_next.title',
            defaultMessage: 'Up next',
          })}
          className={cn('shrink-0 gap-1 rounded-lg p-1 hidden md:flex', bg1)}
          data-testid="learn-up-next-tabs"
        >
          {tabs.map((tab) => {
            const isActive = tab.id === activeTab?.id;
            return (
              <Button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                variant="transparent"
                color="transparent"
                onClick={() => setActiveTabId(tab.id)}
                data-testid={`learn-up-next-tab-${tab.id}`}
                className={cn(
                  'flex-1 rounded-md py-2 text-sm font-semibold',
                  isActive ? cn(bg0, text5, 'shadow-sm') : text5,
                )}
              >
                {tab.label}
              </Button>
            );
          })}
        </Flex>
      )}

      <Box className="min-h-0 flex-1 overflow-y-auto">{activeTab?.content}</Box>
    </Card>
  );
};

export default UpNextPanel;
